import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const STATUS_COLORS = {
  "Yeni": "bg-amber-500/15 text-amber-400",
  "İletişimde": "bg-blue-500/15 text-blue-400",
  "Teklif Verildi": "bg-emerald-500/15 text-emerald-400",
  "Kapandı": "bg-neutral-500/15 text-neutral-400",
};

export default function QuotesPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set("status", status);
    api.get(`/admin/quotes?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const setQuoteStatus = async (quoteId, newStatus) => {
    try {
      await api.put(`/admin/quotes/${quoteId}`, { status: newStatus });
      toast.success("Durum güncellendi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-quotes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Teklif Talepleri</h1>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="h-10 px-3 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-neutral-300 focus:outline-none focus:border-blue-500"
          data-testid="quote-status-filter">
          <option value="">Tümü ({data?.total ?? 0})</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
        {data?.items?.length ? data.items.map((qt) => (
          <div key={qt.quote_id} className="px-5 py-4 border-b border-[#272B33]/50 last:border-0" data-testid={`quote-row-${qt.quote_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm text-white font-semibold truncate">
                  {qt.product_slug ? (
                    <Link to={`/urun/${qt.product_slug}`} target="_blank" className="hover:text-blue-400">{qt.product_name}</Link>
                  ) : qt.product_name}
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  {qt.name} · {qt.email} · {qt.phone}
                </p>
                {qt.message && <p className="text-xs text-neutral-500 mt-1.5 whitespace-pre-line">{qt.message}</p>}
                <p className="text-[10px] text-neutral-600 font-mono-ed mt-1.5">{new Date(qt.created_at).toLocaleString("tr-TR")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[qt.status] || ""}`} data-testid={`quote-status-${qt.quote_id}`}>{qt.status}</span>
                <select value={qt.status} onChange={(e) => setQuoteStatus(qt.quote_id, e.target.value)}
                  className="h-9 px-2 bg-[#0F1115] border border-[#272B33] rounded-lg text-xs text-neutral-300 focus:outline-none focus:border-blue-500"
                  data-testid={`quote-status-select-${qt.quote_id}`}>
                  {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
        )) : (
          <p className="text-center text-neutral-500 text-sm py-12" data-testid="quotes-empty">Henüz teklif talebi yok</p>
        )}
      </div>

      {data?.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: data.pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-blue-600 text-white" : "bg-[#181B20] border border-[#272B33] text-neutral-400"}`}
              data-testid={`quotes-page-${i + 1}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
